// src/components/matches/NoMatchesView.js
import React, { useState, useEffect } from 'react';
import {
  collection,
  query,
  where,
  onSnapshot,
  setDoc,
  doc,
  getDocs,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  arrayUnion,
} from 'firebase/firestore';

import { db } from '../../utils/firebase';
import { getImageUrl } from '../../utils/imageUtils';
import AgendaModal from './AgendaModal';
import { formatDisplayDate } from '../../utils/dateUtils';
import { useLocation } from 'react-router-dom';

const NoMatchesView = ({
  onChooseAnother = () => {},
  currentTrailId: propTrailId,
  currentUserId: propUserId,
}) => {
  const location = useLocation();
  const currentTrailId = propTrailId || location.state?.currentTrailId;
  const currentUserId = propUserId || location.state?.currentUserId;

  const [showAgendaModal, setShowAgendaModal] = useState(false);
  const [trailEvents, setTrailEvents] = useState([]);
  const [userProfiles, setUserProfiles] = useState({});
  const [ownCreatedEvents, setOwnCreatedEvents] = useState([]);
  const [selectedProfile, setSelectedProfile] = useState(null);

  // --- Public "searching" cards for this trail (one per user)
  useEffect(() => {
    if (!currentTrailId) return;

    const matchesRef = collection(db, 'matches');
    const qSearchers = query(
      matchesRef,
      where('trail_id', '==', currentTrailId),
      where('status', '==', 'searching')
    );

    const unsub = onSnapshot(qSearchers, async (snapshot) => {
      const events = [];
      const userIds = new Set();
      const seenUsers = new Set();

      snapshot.forEach((d) => {
        const m = d.data();
        const uid = m?.profile_ids?.id1;
        if (!uid) return;
        if (uid === currentUserId) return; // don’t show my own search here
        if (seenUsers.has(uid)) return;    // only first per user
        seenUsers.add(uid);
        userIds.add(uid);
        events.push({ id: d.id, ...m });
      });

      // Fetch mini profiles for the cards
      const usersSnap = await getDocs(collection(db, 'users'));
      const profiles = {};
      usersSnap.forEach((d) => {
        if (userIds.has(d.id)) profiles[d.id] = { id: d.id, ...d.data() };
      });

      setUserProfiles(profiles);
      setTrailEvents(events);
    });

    return () => unsub();
  }, [currentTrailId, currentUserId]);

  // --- My (owner) events list section
  useEffect(() => {
    if (!currentUserId) return;

    const qMine = query(
      collection(db, 'matches'),
      where('profile_ids.id1', '==', currentUserId)
    );

    const unsub = onSnapshot(qMine, (snapshot) => {
      const myEvents = snapshot.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter(
          (ev) =>
            ev.start_time &&
            ev.end_time &&
            ev.status === 'active' &&
            ev.createdManually === true
        );

      setOwnCreatedEvents(myEvents);
    });

    return () => unsub();
  }, [currentUserId]);

  // --- Delete an event I created in the "My Created Events" section
  const handleDeleteEvent = async (matchId) => {
    if (!window.confirm('Are you sure you want to delete this event?')) return;
    try {
      await deleteDoc(doc(db, 'matches', matchId));
    } catch (err) {
      console.error('Delete error:', err);
    }
  };

  // --- Create my own searching slot (from “Propose a Date”)
  const handleConfirmEvent = async ({ start, end }) => {
    try {
      const matchId = `${currentTrailId}_${currentUserId}_${Date.now()}`;
      await setDoc(doc(db, 'matches', matchId), {
        trail_id: currentTrailId,
        status: 'searching',
        start_time: start,
        end_time: end,
        profile_ids: { id1: currentUserId, id2: null },
        participants: [currentUserId],
        createdAt: serverTimestamp(),
      });
      setShowAgendaModal(false);
    } catch (err) {
      console.error('Error creating match:', err);
    }
  };

  // --- Ensure I (current user) have a "searching" doc for this trail/time; create if missing
  async function ensureOwnSearching({ trailId, start, end }) {
    const qMineSearching = query(
      collection(db, 'matches'),
      where('profile_ids.id1', '==', currentUserId),
      where('trail_id', '==', trailId),
      where('status', '==', 'searching')
    );
    const snaps = await getDocs(qMineSearching);

    let existingId = null;
    snaps.forEach((d) => {
      const m = d.data();
      if (m?.start_time?.seconds === start?.seconds) existingId = d.id;
    });
    if (existingId) return existingId;

    const newId = `${trailId}_${currentUserId}_${Date.now()}`;
    await setDoc(doc(db, 'matches', newId), {
      trail_id: trailId,
      status: 'searching',
      start_time: start,
      end_time: end,
      profile_ids: { id1: currentUserId, id2: null },
      participants: [currentUserId],
      createdAt: serverTimestamp(),
    });
    return newId;
  }

  // --- Send invite (update MY doc from searching → pending with id2 = invitee)
  const handleInvite = async (inviteeUid, _otherMatchId, event) => {
    try {
      if (!currentUserId) return;

      const myMatchId = await ensureOwnSearching({
        trailId: event.trail_id,
        start: event.start_time,
        end: event.end_time,
      });

      await updateDoc(doc(db, 'matches', myMatchId), {
        status: 'pending',
        'profile_ids.id2': inviteeUid,
        participants: arrayUnion(inviteeUid),
        invitedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      alert('Invite sent!');
    } catch (err) {
      console.error('Error sending invite:', err);
      alert(err.message || 'Invite failed');
    }
  };

  return (
    <div className="min-h-screen bg-white p-6 text-center">
      {showAgendaModal && (
        <AgendaModal
          onClose={() => setShowAgendaModal(false)}
          onConfirm={handleConfirmEvent}
        />
      )}

      {trailEvents.length === 0 && (
        <div className="mb-8">
          <div className="w-16 h-16 mx-auto mb-6">
            <svg viewBox="0 0 24 24" className="text-purple-600">
              <path fill="currentColor" d="M12 2L2 12h3v8h14v-8h3L12 2z" />
            </svg>
          </div>
          <h2 className="text-2xl font-semibold mb-2">
            Oops, no buddies here yet...
          </h2>
          <p className="text-gray-600">
            But guess what? You can still propose your own date, pick another
            trail, or check who sent you a request!
          </p>
        </div>
      )}

      {ownCreatedEvents.length > 0 && (
        <div className="mt-10 text-left">
          <h3 className="text-xl font-bold mb-3">My Created Events</h3>
          <div className="space-y-3">
            {ownCreatedEvents.map((event) => (
              <div
                key={event.id}
                className="bg-gray-50 p-4 rounded shadow-sm flex justify-between items-center"
              >
                <div>
                  <p className="font-semibold">{event.trail_id}</p>
                  <p className="text-sm text-gray-600">
                    {formatDisplayDate(event.start_time)}
                  </p>
                </div>
                <button
                  onClick={() => handleDeleteEvent(event.id)}
                  className="text-sm text-red-600 hover:underline"
                >
                  Delete
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {trailEvents.length > 0 &&
        (() => {
          // Dedupe: one card per user
          const unique = new Map();
          trailEvents.forEach((ev) => {
            const uid = ev?.profile_ids?.id1;
            if (uid && !unique.has(uid)) unique.set(uid, ev);
          });
          const list = Array.from(unique.values());

          return (
            <div className="mt-10">
              <h3 className="text-xl font-bold mb-4">Your Best Matches</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
                {list.map((event) => {
                  const u = userProfiles[event.profile_ids.id1];
                  if (!u) return null;

                  const activity = 'hiking';
                  const skillLevels = { beginner: 1, intermediate: 2, advanced: 3 };
                  const u1 = skillLevels[u.skills?.[activity] || 'beginner'];
                  const u2 = skillLevels[userProfiles[currentUserId]?.skills?.[activity] || 'beginner'];
                  const skillDiff = Math.abs(u1 - u2);
                  const skillCompatibility = (1 - skillDiff / 2) * 100;

                  const prefScore = (() => {
                    let s = 0;
                    const me = userProfiles[currentUserId] || {};
                    if (u.preferences?.pace === me.preferences?.pace) s += 20;
                    if (u.preferences?.communication === me.preferences?.communication) s += 15;
                    if (u.preferences?.adventureLevel === me.preferences?.adventureLevel) s += 15;
                    return s;
                  })();

                  const totalCompatibility = Math.round(
                    (skillCompatibility + prefScore) / 2
                  );

                  return (
                    <React.Fragment key={event.id}>
                      <div
                        className="bg-gray-100 p-4 rounded shadow text-left cursor-pointer hover:bg-gray-200 transition"
                        onClick={() =>
                          setSelectedProfile({
                            user: u,
                            event,
                            compatibility: totalCompatibility,
                          })
                        }
                      >
                        <img
                          src={getImageUrl(u.image)}
                          alt={u.name}
                          className="w-full h-32 object-cover rounded"
                        />
                        <h4 className="text-lg font-semibold mt-2">
                          {u.name || 'Anonymous'}
                        </h4>
                        <p className="text-sm text-gray-600">
                          Compatibility: {totalCompatibility}%<br />
                          Skill Level: {u.skills?.[activity] || 'Not specified'}
                          <br />
                          {new Date(
                            event.start_time.seconds * 1000
                          ).toLocaleDateString('en-US', {
                            weekday: 'long',
                            month: 'long',
                            day: 'numeric',
                          })}
                          ,{' '}
                          {new Date(
                            event.start_time.seconds * 1000
                          ).toLocaleTimeString('en-US', {
                            hour: 'numeric',
                            minute: '2-digit',
                          })}
                        </p>
                      </div>

                      {selectedProfile && (
                        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
                          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 relative">
                            <button
                              onClick={() => setSelectedProfile(null)}
                              className="absolute top-2 right-2 text-gray-500 hover:text-black text-xl"
                            >
                              &times;
                            </button>

                            <img
                              src={getImageUrl(selectedProfile.user.image)}
                              alt={selectedProfile.user.name}
                              className="w-full h-40 object-cover rounded mb-4"
                            />

                            <h2 className="text-2xl font-bold mb-2">
                              {selectedProfile.user.name}
                            </h2>
                            <p className="text-gray-700 mb-1">
                              Compatibility: {selectedProfile.compatibility}%
                            </p>
                            <p className="text-gray-700 mb-1">
                              Skill Level:{' '}
                              {selectedProfile.user.skills?.hiking ||
                                'Not specified'}
                            </p>
                            <p className="text-gray-700 mb-1">
                              Pace:{' '}
                              {selectedProfile.user.preferences?.pace || 'N/A'}
                            </p>
                            <p className="text-gray-700 mb-1">
                              Communication:{' '}
                              {selectedProfile.user.preferences?.communication ||
                                'N/A'}
                            </p>
                            <p className="text-gray-700 mb-1">
                              Adventure Level:{' '}
                              {selectedProfile.user.preferences?.adventureLevel ||
                                'N/A'}
                            </p>
                            <p className="text-gray-700 mb-1">
                              Instagram: @
                              {selectedProfile.user.instagram || 'N/A'}
                            </p>

                            <p className="text-gray-700 mt-3">
                              {new Date(
                                selectedProfile.event.start_time.seconds * 1000
                              ).toLocaleDateString('en-US', {
                                weekday: 'long',
                                month: 'long',
                                day: 'numeric',
                              })}
                              ,{' '}
                              {new Date(
                                selectedProfile.event.start_time.seconds * 1000
                              ).toLocaleTimeString('en-US', {
                                hour: 'numeric',
                                minute: '2-digit',
                              })}
                            </p>

                            <button
                              onClick={() => {
                                handleInvite(
                                  selectedProfile.user.id,
                                  selectedProfile.event.id,
                                  selectedProfile.event
                                );
                                setSelectedProfile(null);
                              }}
                              className="w-full mt-4 bg-purple-600 text-white py-2 rounded hover:bg-purple-700"
                            >
                              Invite to Hike
                            </button>
                          </div>
                        </div>
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
            </div>
          );
        })()}

      <div className="space-y-4">
        <p className="text-gray-600">
          Didn’t find your match? Not to worry, you can also...
        </p>

        <div className="w-full max-w-md mx-auto">
          <button
            onClick={() => setShowAgendaModal(true)}
            className="w-full bg-gradient-to-r from-[#6B01D1] to-[#F2787A] text-white rounded-full py-3 font-semibold text-lg transition-transform duration-300 hover:scale-105 hover:shadow-xl"
          >
            Propose a Date
          </button>
        </div>

        <div className="w-full max-w-md mx-auto">
          <button
            onClick={() => onChooseAnother('home')}
            className="w-full bg-[#D7D7D7] text-black rounded-full py-3 font-semibold text-lg transition-transform duration-300 hover:scale-105 hover:shadow-xl"
          >
            Choose another trail
          </button>
        </div>

        <div className="w-full max-w-md mx-auto">
          <button
            onClick={() => onChooseAnother('matches-page')}
            className="w-full border-2 border-gray-700 text-gray-700 rounded-full py-3 font-semibold hover:bg-purple-50 transition-colors"
          >
            View My Matches
          </button>
        </div>
      </div>
    </div>
  );
};

export default NoMatchesView;
